import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Eye,
  ChevronUp,
  ChevronDown,
  Trash2,
  Search,
  X,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";

interface Lab {
  id: string;
  title: string;
  description: string | null;
  order_number: number;
  series_id: string;
  series: {
    id: string;
    title: string;
    code: string;
    type: string;
  };
}

type SortField = "title" | "series" | "order";
type SortDirection = "asc" | "desc";

const Labs: React.FC = () => {
  const [labs, setLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(true);
  const { userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const [sortField, setSortField] = useState<SortField>("series");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [seriesOptions, setSeriesOptions] = useState<
    { id: string; title: string; code: string }[]
  >([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSeriesForAdd, setSelectedSeriesForAdd] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchLabs();
    fetchSeriesOptions();
  }, [searchTerm, selectedSeries, sortField, sortDirection]);

  const fetchSeriesOptions = async () => {
    try {
      const { data, error } = await supabase
        .from("series")
        .select("id, title, code")
        .eq("type", "series")
        .order("title");

      if (error) throw error;
      setSeriesOptions(data || []);
    } catch (error) {
      console.error("Error fetching series options:", error);
    }
  };

  const fetchLabs = async () => {
    try {
      let query = supabase.from("labs").select(`
        *,
        series:series_id (
          id,
          title,
          code,
          type
        )
      `);

      if (searchTerm) {
        query = query.or(
          `title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
        );
      }

      if (selectedSeries) {
        query = query.eq("series_id", selectedSeries);
      }

      // Only sort non-series fields in the query
      if (sortField !== "series") {
        const orderField = sortField === "order" ? "order_number" : sortField;
        query = query.order(orderField, { ascending: sortDirection === "asc" });
      }

      const { data, error } = await query;
      if (error) throw error;

      // Only show labs belonging to type='series' programs
      const seriesOnlyData = data.filter(
        (lab) => lab.series?.type === "series"
      );

      // Only sort series field in memory since it's a nested field
      let sortedData = seriesOnlyData;
      if (sortField === "series") {
        sortedData = [...seriesOnlyData].sort((a, b) => {
          const multiplier = sortDirection === "asc" ? 1 : -1;
          // First compare series titles
          const seriesCompare = (a.series?.title || "").localeCompare(
            b.series?.title || ""
          );
          // If series titles are equal, compare order numbers
          if (seriesCompare === 0) {
            return a.order_number - b.order_number;
          }
          return multiplier * seriesCompare;
        });
      }

      setLabs(sortedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching labs:", error);
      toast.error("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this lab?")) {
      try {
        // First, get all file resources for this lab
        const { data: resources } = await supabase
          .from("lab_resources")
          .select("*")
          .eq("lab_id", id);

        // Delete any files from storage
        if (resources) {
          const filePaths = resources
            .filter((r) => r.type === "file")
            .map((r) => r.file_path)
            .filter((path): path is string => path !== null);

          if (filePaths.length > 0) {
            await supabase.storage.from("lab-resources").remove(filePaths);
          }
        }

        // Then delete the lab (this will cascade delete resources due to FK constraint)
        const { error } = await supabase.from("labs").delete().eq("id", id);

        if (error) throw error;

        setLabs(labs.filter((lab) => lab.id !== id));
        toast.success("Deleted.");
      } catch (error) {
        console.error("Error deleting lab:", error);
        toast.error("Something went wrong.");
      }
    }
  };

  const getSortedLabs = () => {
    return [...labs].sort((a, b) => {
      const multiplier = sortDirection === "asc" ? 1 : -1;

      switch (sortField) {
        case "title":
          return multiplier * a.title.localeCompare(b.title);
        case "series":
          return (
            multiplier *
            (a.series?.title || "").localeCompare(b.series?.title || "")
          );
        case "order":
          return multiplier * (a.order_number - b.order_number);
        default:
          return 0;
      }
    });
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedSeries(null);
  };

  const handleAddLab = () => {
    if (selectedSeriesForAdd) {
      navigate(`/series/${selectedSeriesForAdd}/labs/new`);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    const isCurrentField = sortField === field;
    return (
      <span
        className={`inline-block ml-1 transition-opacity ${
          isCurrentField ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        {sortDirection === "asc" && isCurrentField ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </span>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="">{ENTITY_NAMES.LAB}s</h1>
        {isAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary mt-4 sm:mt-0"
          >
            <Plus className="h-5 w-5" />
            Add {ENTITY_NAMES.LAB}
          </button>
        )}
      </div>

      {/* Series Selection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="">Select Series</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-neutral-500 hover:text-neutral-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              Select the series you want to add a lab to:
            </p>
            <select
              className="input w-full mb-4"
              value={selectedSeriesForAdd}
              onChange={(e) => setSelectedSeriesForAdd(e.target.value)}
            >
              <option value="">Select a series...</option>
              {seriesOptions.map((series) => (
                <option key={series.id} value={series.id}>
                  {series.code} - {series.title}
                </option>
              ))}
            </select>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLab}
                disabled={!selectedSeriesForAdd}
                className="btn btn-primary"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-4 border-b border-neutral-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 py-2 border border-neutral-300 rounded-md"
                placeholder="Search by lab name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <select
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-neutral-300 rounded-md bg-white text-sm text-neutral-700"
                  value={selectedSeries || ""}
                  onChange={(e) => setSelectedSeries(e.target.value || null)}
                >
                  <option value="">All Series</option>
                  {seriesOptions.map((series) => (
                    <option key={series.id} value={series.id}>
                      {series.title}
                    </option>
                  ))}
                </select>
              </div>

              {(searchTerm || selectedSeries) && (
                <button onClick={resetFilters} className="btn btn-outline">
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700 group"
                  onClick={() => handleSort("title")}
                >
                  <span className="inline-flex items-center">
                    Name
                    <SortIcon field="title" />
                  </span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700 group"
                  onClick={() => handleSort("series")}
                >
                  <span className="inline-flex items-center">
                    Series
                    <SortIcon field="series" />
                  </span>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {loading ? (
                Array(6)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      {Array(4)
                        .fill(0)
                        .map((_, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-6 py-4 whitespace-nowrap"
                          >
                            <div className="h-4 bg-neutral-200 rounded animate-pulse"></div>
                          </td>
                        ))}
                    </tr>
                  ))
              ) : getSortedLabs().length > 0 ? (
                getSortedLabs().map((lab) => (
                  <tr key={lab.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium">
                        <Link
                          to={`/series/${lab.series_id}/labs/${lab.id}`}
                          className=""
                        >
                          {lab.title}
                        </Link>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Link
                        to={`/series/${lab.series_id}/labs/${lab.id}`}
                        className=""
                      >
                        Lab {lab.order_number}
                      </Link>{" "}
                      of{" "}
                      <Link to={`/series/${lab.series_id}`} className="">
                        {lab.series?.code}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/series/${lab.series_id}/labs/${lab.id}`}
                          className="icon-view"
                          title="View details"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                        {isAdmin && (
                          <>
                            <Link
                              to={`/series/${lab.series_id}/labs/${lab.id}/edit`}
                              className="icon-edit"
                              title="Edit lab"
                            >
                              <Edit className="h-5 w-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(lab.id)}
                              className="icon-delete"
                              title="Delete lab"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-neutral-500"
                  >
                    No labs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Labs;
